/**
 * Phone number validation utilities for UK phone numbers
 * Supports formats: +44 7xxx xxxxxx, 07xxx xxxxxx, +447xxxxxxxxx, 07xxxxxxxxx
 */

export class PhoneValidator {
  /**
   * Validate UK phone number
   * Accepts:
   * - +44 7xxx xxxxxx (with spaces)
   * - +447xxxxxxxxx (no spaces)
   * - 07xxx xxxxxx (with spaces)
   * - 07xxxxxxxxx (no spaces)
   * - +44 (0)7xxx xxxxxx (alternative format)
   *
   * @param phone - Phone number to validate
   * @returns true if valid UK phone number
   */
  static isValidUK(phone: string): boolean {
    if (!phone || typeof phone !== "string") return false;

    // Remove all spaces and hyphens
    const cleaned = phone.replace(/[\s\-()]/g, "");

    // UK phone number patterns
    const patterns = [
      /^\+447\d{9}$/, // +447xxxxxxxxx (11 digits total)
      /^07\d{9}$/, // 07xxxxxxxxx (10 digits total)
      /^\+44\(0\)7\d{9}$/, // +44(0)7xxxxxxxxx
    ];

    return patterns.some((pattern) => pattern.test(cleaned));
  }

  /**
   * Format phone number to standard format: +44 7xxx xxxxxx
   * @param phone - Phone number to format
   * @returns Formatted phone number or original if invalid
   */
  static formatToStandard(phone: string): string {
    if (!phone) return "";

    // Remove all spaces, hyphens, and parentheses
    let cleaned = phone.replace(/[\s\-()]/g, "");

    // Handle +44(0) format
    if (cleaned.startsWith("+440")) {
      cleaned = "+44" + cleaned.substring(4);
    }

    // Convert 07 to +447
    if (cleaned.startsWith("07")) {
      cleaned = "+447" + cleaned.substring(2);
    }

    // Ensure it starts with +44
    if (!cleaned.startsWith("+44")) {
      return phone; // Return original if can't format
    }

    // Format as +44 7xxx xxxxxx
    const match = cleaned.match(/^\+44(\d{10})$/);
    if (match) {
      const digits = match[1];
      return `+44 ${digits.substring(0, 4)} ${digits.substring(4)}`;
    }

    return phone;
  }

  /**
   * Format phone number to E.164 format: +447xxxxxxxxx
   * @param phone - Phone number to format
   * @returns E.164 formatted phone number or null if invalid
   */
  static formatToE164(phone: string): string | null {
    if (!this.isValidUK(phone)) return null;

    // Remove all spaces, hyphens, and parentheses
    let cleaned = phone.replace(/[\s\-()]/g, "");

    // Handle +44(0) format
    if (cleaned.startsWith("+440")) {
      cleaned = "+44" + cleaned.substring(4);
    }

    // Convert 07 to +447
    if (cleaned.startsWith("07")) {
      cleaned = "+447" + cleaned.substring(2);
    }

    return cleaned;
  }

  /**
   * Get error message for invalid phone number
   * @param phone - Phone number to validate
   * @returns Error message or null if valid
   */
  static getErrorMessage(phone: string): string | null {
    if (!phone) return null; // Empty is allowed (optional field)

    if (typeof phone !== "string") {
      return "Phone number must be a string";
    }

    const cleaned = phone.replace(/[\s\-()]/g, "");

    if (cleaned.length === 0) {
      return null; // Empty is allowed
    }

    if (cleaned.length < 10) {
      return "Phone number is too short";
    }

    if (cleaned.length > 13) {
      return "Phone number is too long";
    }

    if (!/^\+?0?7\d{9}$/.test(cleaned) && !/^\+447\d{9}$/.test(cleaned)) {
      return "Please enter a valid UK phone number (format: +44 7xxx xxxxxx or 07xxx xxxxxx)";
    }

    if (!this.isValidUK(phone)) {
      return "Please enter a valid UK phone number";
    }

    return null;
  }

  /**
   * Sanitize phone number (remove invalid characters)
   * @param phone - Phone number to sanitize
   * @returns Sanitized phone number
   */
  static sanitize(phone: string): string {
    if (!phone) return "";

    // Allow only: +, 0-9, spaces, hyphens, parentheses
    return phone.replace(/[^\d+\s\-()]/g, "");
  }

  /**
   * Check if phone number is from UK
   * @param phone - Phone number to check
   * @returns true if UK phone number
   */
  static isUK(phone: string): boolean {
    return this.isValidUK(phone);
  }

  /**
   * Get phone number info
   * @param phone - Phone number to analyze
   * @returns Object with phone number info
   */
  static getInfo(phone: string): {
    isValid: boolean;
    formatted: string;
    e164: string | null;
    country: string;
    type: "mobile" | "unknown";
  } {
    const isValid = this.isValidUK(phone);
    const formatted = this.formatToStandard(phone);
    const e164 = this.formatToE164(phone);

    return {
      isValid,
      formatted,
      e164,
      country: "UK",
      type: "mobile",
    };
  }

  /**
   * Validate and format phone number
   * @param phone - Phone number to validate and format
   * @returns Object with validation result and formatted number
   */
  static validateAndFormat(phone: string): {
    isValid: boolean;
    formatted: string | null;
    error: string | null;
  } {
    const error = this.getErrorMessage(phone);
    const isValid = error === null && phone.length > 0;

    return {
      isValid,
      formatted: isValid ? this.formatToStandard(phone) : null,
      error,
    };
  }

  /**
   * Ensure phone number has the +44 country code prefix.
   * Unlike formatToE164, this does not require strict validation —
   * it simply prepends +44 if no country code is detected.
   * Use this before sending phone numbers to the API backend.
   *
   * @param phone - Phone number to normalize
   * @param defaultPrefix - Country code prefix (default: "+44")
   * @returns Phone number with country code prefix
   */
  static ensureCountryCode(
    phone: string,
    defaultPrefix: string = "+44",
  ): string {
    if (!phone) return phone;

    // Remove all spaces, hyphens, and parentheses for analysis
    const cleaned = phone.replace(/[\s\-()]/g, "");

    // Already has a country code
    if (cleaned.startsWith("+")) return cleaned;

    // Starts with 0 (e.g. 07xxx) — strip leading 0 and prepend prefix
    if (cleaned.startsWith("0")) {
      return `${defaultPrefix}${cleaned.substring(1)}`;
    }

    // Raw digits (e.g. 7xxx) — prepend prefix directly
    return `${defaultPrefix}${cleaned}`;
  }
}
